// Shared API host resolver for CDN mode
// Resolves API base URL by pinging candidate subdomains (e.g., api.example.com)
// Exposes: ready (Promise), getApiBaseUrl(), buildApiUrl(path), hasApi()

// Subdomain to use for API host (e.g., www.example.com → api.example.com)
const API_SUBDOMAIN = 'api';
// Frontend subdomain prefix to replace with API_SUBDOMAIN (e.g., www → api)
const FRONTEND_PREFIX = 'www';
let apiBaseUrl = null;
let apiResolved = false;

function buildApiHostname(hostname) {
  if (hostname.startsWith(FRONTEND_PREFIX + '.')) {
    return API_SUBDOMAIN + '.' + hostname.slice(FRONTEND_PREFIX.length + 1);
  }
  return `${API_SUBDOMAIN}.${hostname}`;
}

function buildCandidateBases() {
  const protocol = window.location.protocol || 'https:';
  const hostWithPort = window.location.host; // includes port in dev
  const hostname = window.location.hostname; // bare hostname for CDN
  const sameOrigin = `${protocol}//${hostWithPort}`;
  const apiHost = buildApiHostname(hostname);
  const cdnHosts = [`${protocol}//${apiHost}`];
  // In localhost/dev, prefer same-origin so MSW SW can intercept requests.
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    return [sameOrigin, ...cdnHosts];
  }
  return cdnHosts;
}

function ping(url, timeoutMs = 5000) {
  return new Promise((resolve) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    fetch(`${url}/ping`, { method: 'GET', cache: 'no-store', signal: controller.signal })
      .then((res) => {
        clearTimeout(id);
        resolve(res.ok);
      })
      .catch(() => {
        clearTimeout(id);
        resolve(false);
      });
  });
}

async function resolveApiBaseUrl() {
  if (apiResolved) return apiBaseUrl;
  const bases = buildCandidateBases();
  for (const base of bases) {
    const ok = await ping(base);
    if (ok) {
      apiBaseUrl = base;
      apiResolved = true;
      window.__apiBaseUrl = apiBaseUrl;
      return apiBaseUrl;
    }
  }
  apiResolved = true;
  apiBaseUrl = null;
  window.__apiBaseUrl = null;
  return null;
}

export function getApiBaseUrl() {
  return apiBaseUrl;
}

export function buildApiUrl(path) {
  if (!apiBaseUrl) return null;
  if (!path.startsWith('/')) return `${apiBaseUrl}/${path}`;
  return `${apiBaseUrl}${path}`;
}

export function hasApi() {
  return Boolean(apiBaseUrl);
}

function collectPageOfferIds() {
  const ids = [];
  document.querySelectorAll('[data-offer-id]').forEach((el) => {
    const id = el.dataset.offerId;
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids;
}

function applyOfferVisitIds(offerVisitIds) {
  if (!offerVisitIds || typeof offerVisitIds !== 'object') return;
  Object.keys(offerVisitIds).forEach((productId) => {
    const el = document.querySelector(`[data-offer-id="${productId}"]`);
    if (el) el.dataset.offerVisitId = String(offerVisitIds[productId]);
  });
}

function collectPagePayload() {
  // Раньше эти параметры (ph, Name:z5g, utm_*, _websiteId, alg и т.д.) читал
  // сервер напрямую из query-строки при рендере страницы. Теперь страница
  // статическая (CDN), поэтому их собирает и пересылает фронтенд —
  // как есть, без повторного декодирования (бэкенд ожидает то же
  // представление значений, что раньше давал $request->query->all()).
  const pageParams = Object.fromEntries(new URLSearchParams(window.location.search || ''));
  return { ...pageParams, idsProducts: collectPageOfferIds() };
}

async function trackVisitWithPageParams() {
  if (!apiBaseUrl) return null;
  const url = buildApiUrl('/api/visit/track');
  const body = JSON.stringify(collectPagePayload());
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    if (data && typeof data.visitId !== 'undefined') {
      window.__visitId = data.visitId;
    }
    if (data && typeof data.offerVisitIds !== 'undefined') {
      window.__visitData = Object.assign({}, window.__visitData || {}, { offerVisitIds: data.offerVisitIds });
      applyOfferVisitIds(data.offerVisitIds);
    }
    if (data && typeof data.serverTime === 'number' && data.serverTime > 0) {
      window.__serverTime = data.serverTime;
    }
    if (data && typeof data.bankiRuInRollout !== 'undefined') {
      window.__bankiRuInRollout = Boolean(data.bankiRuInRollout);
    }
    // Keep any additional fields for future use and expose them to the frontend
    const { name, amount, restamount, mfoscore, fullamount } = data || {};
    const fields = { name: name ?? null, amount: typeof amount === 'number' ? amount : (amount ? Number(amount) : null), restamount: typeof restamount === 'number' ? restamount : (restamount ? Number(restamount) : null), mfoscore: typeof mfoscore === 'number' ? mfoscore : (mfoscore ? Number(mfoscore) : null), fullamount: typeof fullamount === 'number' ? fullamount : (fullamount ? Number(fullamount) : null) };
    window.__visitData = Object.assign({}, window.__visitData || {}, { fields });
    try {
      const evt = new CustomEvent('visit:tracked', { detail: { visitId: window.__visitId, offerVisitIds: window.__visitData.offerVisitIds, fields, serverTime: window.__serverTime ?? null, bankiRuInRollout: window.__bankiRuInRollout ?? null } });
      window.dispatchEvent(evt);
      document.dispatchEvent(evt);
    } catch (_) {}
    return data;
  } catch (_) {
    return null;
  }
}

export const ready = (async () => {
  // В dev дождёмся готовности MSW, если промис выставлен глобально, чтобы /ping перехватывался корректно
  try {
    if (typeof window !== 'undefined' && window.__mswReadyPromise && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.endsWith('.localhost'))) {
      await window.__mswReadyPromise.catch(() => {});
    }
  } catch (_) {}
  const base = await resolveApiBaseUrl();
  if (!base) {
    // No API hosts — switch to offline
    window.__workOffline = true;
    return null;
  }
  // Track visit before anything else
  await trackVisitWithPageParams();
  return base;
})();
